import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Shield, Users, Clock, CheckCircle2, XCircle, 
  Loader2, RefreshCw, ChevronRight, Award,
  BookOpen, Target, AlertCircle, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GuardianAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, reqRes, evalRes] = await Promise.all([
        axios.get(`${API}/admin/guardian-dashboard`),
        axios.get(`${API}/admin/guardian-access-requests`),
        axios.get(`${API}/admin/guardian-evaluations`)
      ]);
      setDashboard(dashRes.data);
      setRequests(reqRes.data.requests || []);
      setEvaluations(evalRes.data.evaluations || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.put(`${API}/admin/guardian-access/${requestId}/approve`);
      toast.success('Acceso aprobado');
      fetchAll();
    } catch (error) {
      toast.error('Error al aprobar');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.put(`${API}/admin/guardian-access/${requestId}/reject`);
      toast.success('Solicitud rechazada');
      fetchAll();
    } catch (error) {
      toast.error('Error al rechazar');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      </AdminLayout>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');

  return (
    <AdminLayout>
      <div className="space-y-6 p-6" data-testid="guardian-admin-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-amber-500/20">
              <Shield className="h-8 w-8 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Objetivo Guardián</h1>
              <p className="text-muted-foreground">Gestión del módulo formativo</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchAll} className="border-amber-500/30">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-amber-500/20">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Aspirantes</p>
                  <p className="text-2xl font-bold text-amber-400">{dashboard?.users?.aspirantes || 0}</p>
                </div>
                <Users className="h-6 w-6 text-amber-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-blue-500/20">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Promesas</p>
                  <p className="text-2xl font-bold text-blue-400">{dashboard?.users?.promesas || 0}</p>
                </div>
                <Target className="h-6 w-6 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-green-500/20">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Guardianes</p>
                  <p className="text-2xl font-bold text-green-400">{dashboard?.users?.guardianes || 0}</p>
                </div>
                <Award className="h-6 w-6 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={`bg-zinc-900/50 ${pendingRequests.length > 0 ? 'border-red-500/30' : 'border-zinc-700'}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Solicitudes pendientes</p>
                  <p className={`text-2xl font-bold ${pendingRequests.length > 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                    {pendingRequests.length}
                  </p>
                </div>
                <Clock className="h-6 w-6 text-zinc-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="requests" className="w-full">
          <TabsList className="bg-zinc-900/50 border border-zinc-800">
            <TabsTrigger value="requests" className="data-[state=active]:bg-amber-500/20">
              Solicitudes de Acceso
              {pendingRequests.length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center">
                  {pendingRequests.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="evaluations" className="data-[state=active]:bg-amber-500/20">
              Evaluaciones
            </TabsTrigger>
          </TabsList>

          {/* Solicitudes */}
          <TabsContent value="requests" className="mt-4 space-y-4">
            {requests.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-8 text-center">
                  <Shield className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-muted-foreground">No hay solicitudes de acceso</p>
                </CardContent>
              </Card>
            ) : (
              requests.map((req) => (
                <Card 
                  key={req.id} 
                  className={`bg-zinc-900/50 ${req.status === 'pending' ? 'border-amber-500/30' : 'border-zinc-800'}`}
                >
                  <CardContent className="pt-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-foreground">{req.nombre}</h3>
                          <Badge 
                            variant="outline"
                            className={
                              req.status === 'pending' ? 'border-amber-500/50 text-amber-400' :
                              req.status === 'approved' ? 'border-green-500/50 text-green-400' :
                              'border-red-500/50 text-red-400'
                            }
                          >
                            {req.status === 'pending' ? 'Pendiente' : 
                             req.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{req.user_email}</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm">
                          <div>
                            <p className="text-xs text-amber-400 font-medium">Motivo</p>
                            <p className="text-foreground/80">{req.motivo}</p>
                          </div>
                          <div>
                            <p className="text-xs text-amber-400 font-medium">Experiencia</p>
                            <p className="text-foreground/80">{req.experiencia_previa || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-amber-400 font-medium">Disponibilidad</p>
                            <p className="text-foreground/80">{req.disponibilidad || '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-amber-400 font-medium">Interés en formarse</p>
                            <p className="text-foreground/80">{req.interes_formarse}</p>
                          </div>
                        </div>
                        
                        <p className="text-xs text-muted-foreground mt-2">
                          Enviada: {new Date(req.created_at).toLocaleDateString('es-ES', { 
                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>

                      {req.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            onClick={() => handleReject(req.id)}
                            disabled={processing === req.id}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Rechazar
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(req.id)}
                            disabled={processing === req.id}
                          >
                            {processing === req.id ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                            )}
                            Aprobar
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Evaluaciones */}
          <TabsContent value="evaluations" className="mt-4 space-y-4">
            {evaluations.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-8 text-center">
                  <BookOpen className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-muted-foreground">No hay evaluaciones registradas</p>
                </CardContent>
              </Card>
            ) : (
              evaluations.map((ev) => (
                <Card 
                  key={ev.id} 
                  className={`bg-zinc-900/50 ${ev.status === 'pending_review' ? 'border-amber-500/30' : 'border-zinc-800'}`}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-foreground">{ev.user_name}</h3>
                        <p className="text-sm text-muted-foreground">{ev.user_email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="border-amber-500/30 text-amber-400">
                            Nivel: {ev.level}
                          </Badge>
                          <Badge 
                            variant="outline"
                            className={
                              ev.status === 'pending_review' ? 'border-amber-500/50 text-amber-400' :
                              ev.resultado === 'apto' ? 'border-green-500/50 text-green-400' :
                              'border-red-500/50 text-red-400'
                            }
                          >
                            {ev.status === 'pending_review' ? 'Pendiente de revisar' : ev.resultado}
                          </Badge>
                        </div>
                      </div>
                      {ev.status === 'pending_review' && (
                        <Button size="sm" variant="outline" className="border-amber-500/30">
                          <Eye className="h-4 w-4 mr-1" />
                          Revisar
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default GuardianAdminPage;
